import { t } from 'i18next';
import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Camera,
  DataTexture,
  Frustum,
  Intersection,
  Matrix4,
  NearestFilter,
  OrthographicCamera,
  PerspectiveCamera,
  Points,
  Ray,
  Raycaster,
  RGBAFormat,
  ShaderMaterial,
  Sphere,
  Texture,
  UnsignedByteType,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';

import { ProgressController } from '../../../../react/utils/progress-controller';
import { PromiseAbortSignal } from '../../../../react/utils/promise-abort-controller';
import { SkyviewCamera } from '../../../cameras/types';
import { CoordinateManager } from '../../../coordinate-manager';
import { closestToRay, toPointPositions } from '../../../utilities/pick-near-cursor';
import { Cesium3dTilesPointsMaterial } from '../cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-points-material';
import { PointCloud } from '../types';
import { CopcOctree, CopcOctreeNode } from './copc-octree';
import { CopcWorkerPool } from './copc-worker-pool';
import { CopcSelectionOptions, CopcView, selectCopcNodes } from './select-copc-nodes';
import { CopcColorBits, CopcHierarchySubtree, CopcMetadata, CopcPointsData, Triple } from './types';
import { buildVisibleNodes, VISIBLE_NODES_WIDTH } from './visible-nodes';

type LoadedNode = { node: CopcOctreeNode; points: Points; material: ShaderMaterial };

export interface CopcLodSettings {
  /**
   * Most points drawn at once. Looking straight down every node in view needs the same level, so
   * more points are in use than in perspective, where the far ones are coarse.
   */
  pointBudget: { perspective: number; orthographic: number };
  /**
   * A node's children load once its points are more CSS pixels apart on screen than this. The
   * points are drawn as large as their spacing, so they cover the gaps at any setting. CSS pixels,
   * so a high resolution screen doesn't load more points for detail no one can tell apart.
   */
  maxSpacingPixels: number;
  /**
   * Sizes each point by the levels drawn where it is, so points don't change size as levels load.
   * Off, a node's points keep its own spacing, which draws fewer pixels.
   */
  adaptivePointSize: boolean;
}

/**
 * Sized for the computers most users have, which slow down from about 1M points: a computer with
 * few cores or little memory draws half as many. deviceMemory is only known in Chromium, and at
 * most 8.
 */
export function defaultCopcLodSettings(
  cores = navigator.hardwareConcurrency || 4,
  memoryGb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
): CopcLodSettings {
  const weak = cores <= 4 || (memoryGb !== undefined && memoryGb <= 4);
  return {
    pointBudget: weak
      ? { perspective: 500_000, orthographic: 1_000_000 }
      : { perspective: 1_000_000, orthographic: 2_000_000 },
    maxSpacingPixels: 5,
    adaptivePointSize: true,
  };
}

/** What a COPC point cloud draws and loads, shown to organization admins in its settings. */
export interface CopcStats {
  drawnPoints: number;
  drawnNodes: number;
  loadedPoints: number;
  requestsInFlight: number;
  downloadedMb: number;
}

/** Points loaded on top of the budget, kept so panning back doesn't load them again. */
const CACHED_POINTS_FACTOR = 2;
/**
 * A node that failed to load is tried again after these delays, which outlast a short network
 * outage, and then given up on.
 */
const NODE_RETRY_DELAYS_MS = [1000, 2000, 4000];

/**
 * A Cloud Optimized Point Cloud: one LAZ file whose points are ordered in an octree, read with
 * HTTP range requests, see https://copc.io. The workers fetch and decompress the nodes, this picks
 * the nodes to draw each frame and draws them with the same material as the 3D Tiles point clouds.
 */
export class CopcPointCloud extends PointCloud {
  /** The COPC point cloud shown, for its settings. Set on activate(), cleared on clearCustom(). */
  static current: CopcPointCloud | undefined;

  /**
   * Shared by every COPC point cloud, so what an organization admin tunes in the point cloud
   * settings holds for the session.
   */
  static settings: CopcLodSettings = defaultCopcLodSettings();

  private pool = new CopcWorkerPool();
  private octree?: CopcOctree;
  private loaded = new Map<string, LoadedNode>();
  private loadingNodes = new Set<string>();
  private loadingPages = new Map<string, Promise<void>>();
  private failedNodes = new Map<string, { attempts: number; retryAt: number }>();
  private colorBits?: CopcColorBits;
  private camera?: Camera;
  /** The canvas height in CSS pixels. */
  private screenHeight = 1080;
  private disposed = false;
  private drawnPoints = 0;
  /** Shared by the drawn nodes' materials, see visible-nodes.ts. */
  private visibleNodes: { value: Texture | null } = { value: null };
  private visibleNodesKeys = '';
  private downloadedBytes = 0;

  /**
   * @param url The COPC's signed url.
   * @param origo The center of the points' bounds, which the points are placed relative to.
   */
  constructor(
    private url: string,
    readonly origo: Vector3,
  ) {
    super();
  }

  /** Reads the header and the root of the hierarchy, and loads the root node. */
  async activate() {
    // From the start, so the point cloud activated last is the current one, also when one before
    // it, of a timeline no longer shown, finishes after it
    CopcPointCloud.current = this;
    const metadata = await this.pool.request<CopcMetadata>({ type: 'metadata', url: this.url });
    this.octree = new CopcOctree(metadata, this.origo);
    this.octree.addSubtree(
      await this.pool.request<CopcHierarchySubtree>({
        type: 'hierarchy',
        url: this.url,
        page: metadata.rootHierarchyPage,
      }),
    );

    // The root's points are a sample of the whole point cloud, so they tell how many bits its
    // colors use before the other nodes load
    const root = this.octree.root;
    if (root && root.hierarchy.pointCount > 0) {
      await this.loadNode(root);
    }
  }

  get stats(): CopcStats {
    let loadedPoints = 0;
    for (const { node } of this.loaded.values()) {
      loadedPoints += node.hierarchy.pointCount;
    }
    return {
      drawnPoints: this.drawnPoints,
      drawnNodes: this.children.length,
      loadedPoints,
      requestsInFlight: this.pool.activeRequests,
      downloadedMb: Math.round(this.downloadedBytes / 1e5) / 10,
    };
  }

  setCameraAndResolution(camera: Camera, renderer: WebGLRenderer) {
    this.camera = camera;

    // The size is (0, 0) on the first frames, before the canvas is sized
    const size = renderer.getSize(new Vector2());
    if (size.x === 0 || size.y === 0) {
      size.set(window.innerWidth || 1280, window.innerHeight || 720);
    }
    this.screenHeight = size.y;
    // The points are sized in the drawing buffer's pixels
    Cesium3dTilesPointsMaterial.instance.shared.screenHeight.value =
      size.y * renderer.getPixelRatio();
  }

  /** Picks the nodes to draw for the camera, loads the missing ones and draws the loaded ones. */
  update() {
    if (!this.octree || !this.camera) {
      return;
    }

    const orthographic = (this.camera as OrthographicCamera).isOrthographicCamera === true;
    const { pointBudget, maxSpacingPixels } = CopcPointCloud.settings;
    const options: CopcSelectionOptions = {
      pointBudget: orthographic ? pointBudget.orthographic : pointBudget.perspective,
      maxSpacingPixels,
    };
    const { nodes, unloadedPages } = selectCopcNodes(
      this.octree,
      this.createView(this.camera, orthographic),
      options,
      new Set(this.children.flatMap((points) => points.userData.copcKey ?? [])),
    );

    for (const key of unloadedPages) {
      void this.loadPage(key);
    }

    // The most important first, and no more than the workers take at once, so the nodes that
    // stop mattering as the camera moves are never requested. A node waits for its parent, so the
    // view fills in from coarse to fine and no detail loads that a move makes useless.
    for (const node of nodes) {
      if (this.pool.activeRequests >= this.pool.capacity) {
        break;
      }
      if (!this.isRequested(node.key) && this.hasParentLoaded(node)) {
        void this.loadNode(node);
      }
    }

    const drawn = nodes.flatMap((node) => this.loaded.get(node.key) ?? []);
    this.drawnPoints = drawn.reduce((sum, { node }) => sum + node.hierarchy.pointCount, 0);
    this.updateVisibleNodes(drawn);
    this.updateDrawMode(drawn);
    this.remove(...this.children);
    if (drawn.length > 0) {
      this.add(...drawn.map(({ points }) => points));
    }

    this.unloadUnused(new Set(nodes.map((node) => node.key)), options.pointBudget);
  }

  loadLodChunks(_camera: SkyviewCamera): Promise<void> {
    // Done in update, every frame
    return Promise.resolve();
  }

  cullLodChunks(_camera: SkyviewCamera): void {
    // Done in update, every frame
  }

  /** Every point near the rays, at the octree's full detail, see the design tools. */
  async getMaxLodChunksFromRays(
    rays: Ray[],
    signal?: PromiseAbortSignal,
    progressController?: ProgressController,
  ): Promise<Points[]> {
    // Also downwards, for the rays that start below the points
    const flip = new Vector3(1, 1, -1);
    const allRays = rays.flatMap((ray) => [
      ray,
      new Ray(ray.origin, ray.direction.clone().multiply(flip)),
    ]);

    return this.loadFullDetail(
      (box) => allRays.some((ray) => ray.intersectsBox(box)),
      signal,
      progressController,
    );
  }

  /** Every point within the box, at the octree's full detail, see the design tools. */
  async getMaxLodChunksWithinBoundingBox(
    bbox: Box3,
    signal?: PromiseAbortSignal,
    progressController?: ProgressController,
  ): Promise<Points[]> {
    return this.loadFullDetail((box) => bbox.intersectsBox(box), signal, progressController);
  }

  /**
   * Picks the point closest to the ray, for rays that aren't from the camera. Rays from the camera
   * pick with pickNearCursor.
   */
  pick(rayCaster: Raycaster): Intersection[] {
    const closest = closestToRay(this.intersect(rayCaster));
    return closest ? [closest] : [];
  }

  intersect(rayCaster: Raycaster): Intersection[] {
    // Points.raycast reads the world matrices without updating them
    this.updateWorldMatrix(true, true);

    const intersections: Intersection[] = [];
    const threshold = rayCaster.params.Points.threshold;
    for (const { node, points } of this.loaded.values()) {
      if (points.parent !== this) {
        continue;
      }
      // Gathers generously, pickNearCursor narrows it down on screen
      rayCaster.params.Points.threshold = node.spacing;
      intersections.push(...rayCaster.intersectObject(points, false));
    }
    rayCaster.params.Points.threshold = threshold;

    return toPointPositions(rayCaster, intersections);
  }

  /** False once cleared, so picking falls back to the ground until the next point cloud loads. */
  available(): boolean {
    return !this.disposed;
  }

  /** The bounds of the points in the scene. */
  getBoundingBox(): Box3 {
    if (!this.octree) {
      return new Box3();
    }

    const { min, max } = this.octree.metadata;
    const projectionOrigo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
    return new Box3(new Vector3(...min), new Vector3(...max)).translate(
      projectionOrigo.clone().negate(),
    );
  }

  getOrigo(): Vector3 {
    return this.origo;
  }

  clearCustom(): void {
    if (CopcPointCloud.current === this) {
      CopcPointCloud.current = undefined;
    }
    this.disposed = true;
    this.pool.dispose();
    this.visibleNodes.value?.dispose();
    this.remove(...this.children);
    for (const key of [...this.loaded.keys()]) {
      this.unloadNode(key);
    }
  }

  private createView(camera: Camera, orthographic: boolean): CopcView {
    const toLocal = new Matrix4()
      .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
      .multiply(this.matrixWorld);

    return {
      frustum: new Frustum().setFromProjectionMatrix(toLocal),
      cameraPosition: this.worldToLocal(camera.getWorldPosition(new Vector3())),
      orthographic,
      focalLength: (camera as PerspectiveCamera | OrthographicCamera).projectionMatrix.elements[5],
      screenHeight: this.screenHeight,
    };
  }

  private isRequested(key: string) {
    const failed = this.failedNodes.get(key);
    return (
      this.loaded.has(key) ||
      this.loadingNodes.has(key) ||
      (failed !== undefined && performance.now() < failed.retryAt)
    );
  }

  /** Whether a node failed to load as many times as it is tried. */
  private hasFailedForGood(key: string) {
    return (this.failedNodes.get(key)?.attempts ?? 0) > NODE_RETRY_DELAYS_MS.length;
  }

  /** Whether the closest ancestor with points is loaded, or failed and won't be. */
  private hasParentLoaded(node: CopcOctreeNode) {
    for (let up = 1; up <= node.depth; up++) {
      const key = `${node.depth - up}-${node.x >> up}-${node.y >> up}-${node.z >> up}`;
      if ((this.octree?.get(key)?.hierarchy.pointCount ?? 0) > 0) {
        return this.loaded.has(key) || this.hasFailedForGood(key);
      }
    }
    return true;
  }

  /** Loads a hierarchy page into the octree, once however many times it's asked for. */
  private loadPage(key: string): Promise<void> {
    const page = this.octree?.getPage(key);
    if (!page) {
      return Promise.resolve();
    }

    let loading = this.loadingPages.get(key);
    if (!loading) {
      loading = this.pool
        .request<CopcHierarchySubtree>({ type: 'hierarchy', url: this.url, page })
        .then((subtree) => this.octree?.addSubtree(subtree))
        .catch((err) => {
          if (!this.disposed) {
            console.warn(`Failed to load the COPC hierarchy page ${key}`, err);
          }
        })
        .finally(() => this.loadingPages.delete(key));
      this.loadingPages.set(key, loading);
    }
    return loading;
  }

  private async loadNode(node: CopcOctreeNode) {
    this.loadingNodes.add(node.key);
    try {
      const data = await this.fetchPoints(node);
      if (!this.disposed) {
        this.loaded.set(node.key, this.createPoints(node, data));
        this.failedNodes.delete(node.key);
      }
    } catch (err) {
      if (!this.disposed) {
        const attempts = (this.failedNodes.get(node.key)?.attempts ?? 0) + 1;
        const delay = NODE_RETRY_DELAYS_MS[attempts - 1] ?? Infinity;
        this.failedNodes.set(node.key, { attempts, retryAt: performance.now() + delay });
        console.warn(`Failed to load the COPC node ${node.key}`, err);
      }
    } finally {
      this.loadingNodes.delete(node.key);
    }
  }

  private async fetchPoints(node: CopcOctreeNode): Promise<CopcPointsData> {
    const metadata = this.octree!.metadata;
    const center = node.center.clone().add(this.origo);
    const data = await this.pool.request<CopcPointsData>({
      type: 'points',
      url: this.url,
      metadata,
      node: node.hierarchy,
      center: center.toArray() as Triple,
      colorBits: this.colorBits,
    });
    this.colorBits ??= data.colorBits;
    this.downloadedBytes += node.hierarchy.pointDataLength;
    return data;
  }

  private createPoints(node: CopcOctreeNode, data: CopcPointsData): LoadedNode {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(data.positions, 3));
    geometry.setAttribute('color', new BufferAttribute(data.colors, 3, true));
    geometry.setAttribute('classification', new BufferAttribute(data.classifications, 1));
    geometry.boundingBox = node.box.clone().translate(node.center.clone().negate());
    geometry.boundingSphere = geometry.boundingBox.getBoundingSphere(new Sphere());

    const material = Cesium3dTilesPointsMaterial.instance.createTileInstance(node.spacing, {
      withoutNormals: true,
      adaptiveOctree: true,
    });
    material.uniforms.visibleNodes = this.visibleNodes;
    material.uniforms.cubeCenter.value.subVectors(node.cubeCenter, node.center);
    material.uniforms.cubeHalfSize.value = node.cubeHalfSize;

    const points = new Points(geometry, material);
    points.position.copy(node.center);
    points.updateMatrix();
    points.userData.copcKey = node.key;

    return { node, points, material };
  }

  /**
   * Describes the drawn nodes to the shader, which sizes each point by the deepest level drawn
   * where it is. A node's points then only shrink where its children fill in between them, not
   * across the whole node, so points don't change size, or leave gaps, as levels load.
   */
  private updateVisibleNodes(drawn: LoadedNode[]) {
    const keys = drawn.map(({ node }) => node.key).join(',');
    if (keys === this.visibleNodesKeys) {
      return;
    }
    this.visibleNodesKeys = keys;

    const { data, count, indices } = buildVisibleNodes(drawn.map(({ node }) => node.key));
    const rows = Math.max(1, Math.ceil(count / VISIBLE_NODES_WIDTH));
    let texture = this.visibleNodes.value as DataTexture | null;
    if (!texture || texture.image.height !== rows) {
      texture?.dispose();
      texture = new DataTexture(
        new Uint8Array(VISIBLE_NODES_WIDTH * rows * 4),
        VISIBLE_NODES_WIDTH,
        rows,
        RGBAFormat,
        UnsignedByteType,
      );
      texture.magFilter = NearestFilter;
      texture.minFilter = NearestFilter;
      this.visibleNodes.value = texture;
    }
    (texture.image.data as Uint8Array).set(data);
    texture.needsUpdate = true;

    for (const { node, material } of drawn) {
      material.uniforms.nodeIndex.value = indices.get(node.key);
    }
  }

  /**
   * Draws the points opaque while the layer is, so the GPU skips the points behind others instead
   * of blending every one of them back to front. The points are many and overlap a lot, so this
   * saves much of the drawing.
   */
  private updateDrawMode(drawn: LoadedNode[]) {
    const transparent = Cesium3dTilesPointsMaterial.instance.shared.opacity.value < 1;
    const { adaptivePointSize } = CopcPointCloud.settings;

    for (const { material } of drawn) {
      if (material.transparent !== transparent) {
        material.transparent = transparent;
        material.needsUpdate = true;
      }
      if ('ADAPTIVE_OCTREE' in material.defines !== adaptivePointSize) {
        if (adaptivePointSize) {
          material.defines.ADAPTIVE_OCTREE = '';
        } else {
          delete material.defines.ADAPTIVE_OCTREE;
        }
        material.needsUpdate = true;
      }
    }
  }

  /** Unloads the nodes that weren't drawn for the longest, once more points are loaded than kept. */
  private unloadUnused(inUse: Set<string>, pointBudget: number) {
    let loadedPoints = 0;
    for (const { node } of this.loaded.values()) {
      loadedPoints += node.hierarchy.pointCount;
    }

    // The map keeps the order nodes were set in, which touch() moves to the end when drawn
    const limit = pointBudget * CACHED_POINTS_FACTOR;
    for (const [key, { node }] of this.loaded) {
      if (loadedPoints <= limit) {
        break;
      }
      if (!inUse.has(key) && key !== this.octree?.root?.key) {
        loadedPoints -= node.hierarchy.pointCount;
        this.unloadNode(key);
      }
    }

    for (const key of inUse) {
      this.touch(key);
    }
  }

  /** Moves a loaded node last in the unload order. */
  private touch(key: string) {
    const loaded = this.loaded.get(key);
    if (loaded) {
      this.loaded.delete(key);
      this.loaded.set(key, loaded);
    }
  }

  private unloadNode(key: string) {
    const loaded = this.loaded.get(key);
    if (!loaded) {
      return;
    }
    this.remove(loaded.points);
    loaded.points.geometry.dispose();
    loaded.material.dispose();
    this.loaded.delete(key);
  }

  /**
   * Loads every node whose box passes the test, from the root to the leaves since each level adds
   * points, into Points of their own that outlive the drawn ones.
   */
  private async loadFullDetail(
    test: (box: Box3) => boolean,
    signal?: PromiseAbortSignal,
    progressController?: ProgressController,
  ): Promise<Points[]> {
    // Rejects rather than returning fewer points, which the design tools would take as all of them
    const cleared = () => new Error('The COPC point cloud was cleared');
    if (this.disposed) {
      throw cleared();
    }
    const octree = this.octree;
    if (!octree?.root) {
      return [];
    }

    // The tests are in the scene, the boxes relative to the origo
    const toScene = this.position;
    const sceneBox = new Box3();
    const passes = (node: CopcOctreeNode) => test(sceneBox.copy(node.box).translate(toScene));

    const nodes: CopcOctreeNode[] = [];
    let level = passes(octree.root) ? [octree.root] : [];
    while (level.length > 0) {
      if (signal?.aborted) {
        return [];
      }
      nodes.push(...level.filter((node) => node.hierarchy.pointCount > 0));

      const unloadedPages = level.flatMap((node) => octree.children(node).unloadedPages);
      await Promise.all(unloadedPages.map((key) => this.loadPage(key)));
      if (this.disposed) {
        throw cleared();
      }
      // A page that failed to load would leave out every node below it
      if (unloadedPages.some((key) => octree.getPage(key))) {
        throw new Error('Failed to load the COPC hierarchy');
      }
      level = level.flatMap((node) => octree.children(node).nodes).filter(passes);
    }

    const points: Points[] = [];
    let next = 0;
    const loadNext = async (): Promise<void> => {
      while (next < nodes.length && !signal?.aborted && !this.disposed) {
        const node = nodes[next++];
        points.push(this.createPoints(node, await this.fetchPoints(node)).points);
        progressController?.callback(
          points.length / nodes.length,
          `${progressController.step} ${t('section.functions.grid.downloadingPointCloud', { ns: 'skyviewDesign' })}`,
        );
      }
    };
    // At least one, so a pool without workers rejects the load instead of returning no points
    await Promise.all(Array.from({ length: Math.max(1, this.pool.capacity) }, loadNext));
    progressController?.incrementStep();

    if (signal?.aborted) {
      return [];
    }
    if (this.disposed) {
      throw cleared();
    }
    return points;
  }
}
